import { useId, useState } from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { useTranslation } from 'react-i18next';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';
import { formatMinor } from 'features/subscription/utils/seat-pools';
import { formatNumber } from 'features/usage/utils/usage-format';
import { amountProblem, creditsFor, parseAmount, toMinor } from '../utils/amount';

interface AmountPickerProps {
  presets: number[];
  minChf: number;
  maxChf: number;
  initialChf: number | null;
  creditsPerChf?: number;
  /** A visible heading above the presets. Without it the presets only carry an aria-label. */
  label?: string;
  /** Shown under the custom amount while it has no problem to report. */
  hint?: string;
  /** `dialog` is the Buy credits look: softer tiles and a full-width custom amount. */
  variant?: 'default' | 'dialog';
  /** The amount to pay in CHF, or null while the custom amount is empty or not valid. */
  onChange: (amountChf: number | null) => void;
}

const tileClass =
  'flex cursor-pointer flex-col items-center justify-center gap-0.5 rounded-xl border border-border bg-background px-3 py-3 text-foreground tabular-nums transition hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-foreground data-[state=checked]:bg-accent data-[state=checked]:shadow-sm';

const dialogTileClass =
  'flex min-h-16 cursor-pointer flex-col items-center justify-center gap-0.5 rounded-section border border-grey-100 bg-background px-3 py-3 text-foreground tabular-nums transition hover:border-foreground/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-foreground data-[state=checked]:bg-grey-50 dark:border-border dark:data-[state=checked]:border-foreground dark:data-[state=checked]:bg-muted';

const subtleText = 'text-grey-500 dark:text-muted-foreground';

export const AmountPicker = ({
  presets,
  minChf,
  maxChf,
  initialChf,
  creditsPerChf,
  label,
  hint,
  variant = 'default',
  onChange,
}: AmountPickerProps) => {
  const { t } = useTranslation();
  const inputId = useId();
  const labelId = useId();
  const problemId = useId();
  const isDialog = variant === 'dialog';
  const [selected, setSelected] = useState<number | null>(initialChf);
  const [custom, setCustom] = useState('');
  const problem = custom ? amountProblem(custom, minChf, maxChf) : null;
  const customAmount = custom && !problem ? parseAmount(custom) : null;
  // "= 1,500 credits" inside the dialog's field, once the amount typed is one that can be bought.
  const customCredits =
    isDialog && creditsPerChf && customAmount !== null ? formatNumber(creditsFor(customAmount, creditsPerChf)) : '';

  const selectPreset = (value: string) => {
    setSelected(Number(value));
    setCustom('');
    onChange(Number(value));
  };

  const changeCustom = (text: string) => {
    // Digits only: up to 4 before the decimal point (the max is CHF 1000) and 2 after it.
    if (!/^\d{0,4}([.,]\d{0,2})?$/.test(text)) return;
    setCustom(text);
    setSelected(null);
    const amount = parseAmount(text);
    const isValid = amount !== null && !amountProblem(text, minChf, maxChf);
    onChange(isValid ? amount : null);
  };

  const problemText = {
    invalid: t('AMOUNT_INVALID', 'Enter an amount in CHF, for example 40 or 12.50.'),
    decimals: t('AMOUNT_DECIMALS', 'Use at most 2 decimals.'),
    minimum: t('AMOUNT_MINIMUM', {
      amount: formatMinor(toMinor(minChf)),
      defaultValue: 'The minimum top-up is {{amount}}.',
    }),
    maximum: t('AMOUNT_MAXIMUM', {
      amount: formatMinor(toMinor(maxChf)),
      defaultValue: 'The maximum top-up is {{amount}}.',
    }),
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2.5">
        {label && (
          <p id={labelId} className="text-sm font-medium text-foreground">
            {label}
          </p>
        )}
        <RadioGroupPrimitive.Root
          value={selected === null ? '' : String(selected)}
          onValueChange={selectPreset}
          aria-label={label ? undefined : t('TOP_UP_AMOUNT', 'Top-up amount')}
          aria-labelledby={label ? labelId : undefined}
          className="grid grid-cols-2 gap-2 sm:grid-cols-4"
        >
          {presets.map((preset) => (
            <RadioGroupPrimitive.Item
              key={preset}
              value={String(preset)}
              className={isDialog ? dialogTileClass : tileClass}
            >
              <span className={cn('text-base', isDialog ? 'font-medium leading-heading' : 'font-semibold')}>
                {formatMinor(toMinor(preset))}
              </span>
              {creditsPerChf && (
                <span className={cn('text-xs', isDialog ? subtleText : 'text-muted-foreground')}>
                  {t('CREDITS_COUNT', {
                    credits: formatNumber(preset * creditsPerChf),
                    defaultValue: '{{credits}} credits',
                  })}
                </span>
              )}
            </RadioGroupPrimitive.Item>
          ))}
        </RadioGroupPrimitive.Root>
      </div>

      <div className={isDialog ? 'space-y-2' : 'space-y-1.5'}>
        <Label htmlFor={inputId} className={cn(isDialog && 'block leading-body')}>
          {t('CUSTOM_AMOUNT', 'Or enter another amount')}
        </Label>
        <div className={cn('relative w-full', !isDialog && 'sm:max-w-56')}>
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute top-1/2 -translate-y-1/2 text-sm',
              isDialog ? cn('left-3.5', subtleText) : 'left-3 text-muted-foreground'
            )}
          >
            CHF
          </span>
          <Input
            id={inputId}
            inputMode="decimal"
            autoComplete="off"
            placeholder={isDialog ? '0' : undefined}
            className={cn(
              'pl-12 tabular-nums',
              isDialog && 'h-11 rounded-xl border-grey-100 bg-background shadow-none dark:border-border',
              customCredits && 'pr-36'
            )}
            value={custom}
            onChange={(event) => changeCustom(event.target.value)}
            aria-invalid={!!problem}
            aria-describedby={problem || hint ? problemId : undefined}
          />
          {customCredits && (
            <span
              className={cn('pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-sm tabular-nums', subtleText)}
            >
              {t('EQUALS_CREDITS', { credits: customCredits, defaultValue: '= {{credits}} credits' })}
            </span>
          )}
        </div>
        <p
          id={problemId}
          role="status"
          className={cn('text-xs', problem ? 'text-destructive' : subtleText)}
        >
          {problem ? problemText[problem] : (hint ?? '')}
        </p>
      </div>
    </div>
  );
};
